import {
  useRef,
  useEffect,
} from 'react';
import type {
  HTMLAttributes,
  DetailedHTMLProps,
} from 'react';
import classNames from 'classnames';

import styles from './Marquee.module.css';

type MarqueeProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  speed?: number;
  reversed?: boolean;
  draggable?: boolean;
};

const Marquee = (props: MarqueeProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    speed = 600,
    reversed = false,
    draggable = false,
    className,
    children,
    ...rest
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const marqueeRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    const marquee = marqueeRef.current;
    const track = trackRef.current;

    if (
      !track ||
      !marquee
    ) return;

    const children = track.querySelectorAll<HTMLDivElement>('[data-child]');


    const DIRECTION = reversed ? -1 : 1;
    const SPEED = speed * DIRECTION / 1000; // px per frame (~36px/s at 60fps)
    const RESUME_MS = 1200;     // idle time before auto-advance resumes

    let raf = null;
    let position = 0;
    let period = 0;             // width of one repeating set (incl. gap)
    let paused = false;
    let resumeTimeout = null;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;

    function subpixelScroll() {
      position += SPEED;

      if (
        position >= period * 2
      ) {
        position -= period;
      } else if (
        position < period
      ) {
        position += period;
      }

      // Split: integer part -> scrollLeft, sub-pixel part -> transform
      const whole = Math.floor(position);
      const fraction  = position - whole;
      marquee.scrollLeft = whole;
      track.style.transform = `translateX(${-fraction}px)`;
    }

    function syncDragPosition() {
      if (
        track.style.transform !== 'translateX(0px)'
      ) track.style.transform = 'translateX(0px)';

      let {
        scrollLeft,
      } = marquee;

      if (
        scrollLeft >= period * 2
      ) {
        scrollLeft -= period;
        marquee.scrollLeft = scrollLeft;
      } else if (
        scrollLeft < period
      ) {
        scrollLeft += period;
        marquee.scrollLeft = scrollLeft;
      }

      position = scrollLeft;
    }

    function tick() {
      if (
        period <= 0
      ) return;

      if (
        !paused
      ) {
        subpixelScroll();
      } else {
        syncDragPosition();
      }

      raf = requestAnimationFrame(tick);
    }

    // The repeating distance = offset between set 1 and set 2. This captures
    // content width + gap exactly, so the wrap jump is truly invisible.
    function measure() {
      period = children[1].offsetLeft - children[0].offsetLeft;
      position = period;
      marquee.scrollLeft = period;
      track.style.transform = 'translateX(0px)';
    }

    function pauseAndScheduleResume() {
      paused = true;

      clearTimeout(resumeTimeout);

      resumeTimeout = setTimeout(() => {
        paused = false;
      }, RESUME_MS);
    }

    const handleScroll = () => {
      if (
        !paused
      ) return;

      pauseAndScheduleResume();
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (
        !dragging
      ) return;

      marquee.scrollLeft = startScroll - (e.clientX - startX);
    };

    const handlePointerDown = (e: PointerEvent) => {
      pauseAndScheduleResume();

      if (
        e.pointerType !== 'mouse'
      ) return;

      dragging = true;
      startX = e.clientX;
      startScroll = marquee.scrollLeft;
      marquee.style.cursor = 'grabbing';
      marquee.setPointerCapture(e.pointerId);
    };

    const handleEndDrag = () => {
      dragging = false;
      marquee.style.cursor = '';
    };

    if (draggable) {
      marquee.addEventListener('wheel', pauseAndScheduleResume, { passive: true });
      marquee.addEventListener('touchstart', pauseAndScheduleResume, { passive: true });
      marquee.addEventListener('keydown', pauseAndScheduleResume);
      marquee.addEventListener('scroll', handleScroll, { passive: true });

      marquee.addEventListener('pointerdown', handlePointerDown);
      marquee.addEventListener('pointermove', handlePointerMove);

      marquee.addEventListener('pointerup', handleEndDrag);
      marquee.addEventListener('pointercancel', handleEndDrag);
    }

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);

    measure();

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      marquee.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      raf = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resumeTimeout);
      resizeObserver.disconnect();

      if (
        !draggable
      ) return;

      marquee.removeEventListener('wheel', pauseAndScheduleResume);
      marquee.removeEventListener('touchstart', pauseAndScheduleResume);
      marquee.removeEventListener('keydown', pauseAndScheduleResume);
      marquee.removeEventListener('scroll', handleScroll);
      marquee.removeEventListener('pointerdown', handlePointerDown);
      marquee.removeEventListener('pointermove', handlePointerMove);
      marquee.removeEventListener('pointerup', handleEndDrag);
      marquee.removeEventListener('pointercancel', handleEndDrag);
    };
  }, [draggable]);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const marqueeClassName = classNames({
    [styles.marquee]: true,
    [styles.draggable]: draggable,
    [className]: !!className,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div
      {...rest}
      ref={marqueeRef}
      className={marqueeClassName}
    >
      <div
        ref={trackRef}
        className={styles.track}
      >
        <div
          className={styles.children}
          data-child="1"
        >
          {children}
        </div>
        <div
          className={styles.children}
          data-child="2"
        >
          {children}
        </div>
        <div
          className={styles.children}
          data-child="2"
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Marquee;
